import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api';

const Conductores = ({ usuario, onLogout }) => {
  const [conductores, setConductores] = useState([]);
  const [usuariosConductor, setUsuariosConductor] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [verInactivos, setVerInactivos] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    nombre: '', cedula: '', licencia: '', telefono: '',
    porcentaje_excedente: '0', usuario_id: ''
  });
  const esAdmin = usuario?.rol === 'admin';

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      // allSettled: conductores es esencial; usuarios es opcional (para el vínculo).
      const [condRes, usrRes] = await Promise.allSettled([
        api.get('/conductores'),
        api.get('/usuarios'),
      ]);
      if (condRes.status === 'rejected') throw condRes.reason;
      const condData = condRes.value.data;
      setConductores(condData);
      const usrData = usrRes.status === 'fulfilled' ? usrRes.value.data : [];
      // Solo usuarios con rol conductor sin conductor vinculado aún
      const conductoresConUsuario = condData
        .filter(c => c.usuario_id)
        .map(c => c.usuario_id);
      setUsuariosConductor(
        usrData.filter(u =>
          u.rol === 'conductor' &&
          u.activo &&
          !conductoresConUsuario.includes(u.id)
        )
      );
      setError('');
    } catch {
      setError('Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const resetForm = () => {
    setForm({ nombre: '', cedula: '', licencia: '', telefono: '', porcentaje_excedente: '0', usuario_id: '' });
    setEditando(null);
    setShowForm(false);
    setError('');
  };

  const abrirEditar = (c) => {
    setForm({
      nombre: c.nombre,
      cedula: c.cedula || '',
      licencia: c.licencia || '',
      telefono: c.telefono || '',
      porcentaje_excedente: c.porcentaje_excedente,
      usuario_id: c.usuario_id || '',
    });
    setEditando(c.id);
    setShowForm(true);
  };

  const guardar = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const datos = { ...form, usuario_id: form.usuario_id || null };
      if (editando) {
        await api.put(`/conductores/${editando}`, datos);
      } else {
        await api.post('/conductores', datos);
      }
      resetForm();
      cargar();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error guardando conductor');
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Desactivar este conductor?')) return;
    try {
      await api.delete(`/conductores/${id}`);
      cargar();
    } catch {
      setError('Error desactivando conductor');
    }
  };

  const restaurar = async (id) => {
    try {
      await api.patch(`/conductores/${id}/restaurar`);
      cargar();
    } catch {
      setError('Error restaurando conductor');
    }
  };

  const activos = conductores.filter(c => c.activo);
  const inactivos = conductores.filter(c => !c.activo);
  const lista = verInactivos ? inactivos : activos;

  // Usuarios disponibles para el select: los libres + el que ya tiene este conductor
  const usuariosDisponibles = editando
    ? [...usuariosConductor, ...conductores
        .filter(c => c.id === editando && c.usuario_id)
        .map(c => ({ id: c.usuario_id, nombre: '(vinculado actual)' }))]
    : usuariosConductor;

  return (
    <Layout usuario={usuario} onLogout={onLogout} title="Conductores">
      <div style={styles.headerRow}>
        <div style={styles.tabs}>
          <button style={{ ...styles.tabBtn, ...(!verInactivos ? styles.tabActive : {}) }} onClick={() => setVerInactivos(false)}>
            Activos ({activos.length})
          </button>
          <button style={{ ...styles.tabBtn, ...(verInactivos ? styles.tabActive : {}) }} onClick={() => setVerInactivos(true)}>
            Inactivos ({inactivos.length})
          </button>
        </div>
        {esAdmin && !verInactivos && (
          <button style={styles.btnAdd} onClick={() => setShowForm(true)}>+ Nuevo Conductor</button>
        )}
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {showForm && (
        <div style={styles.formCard}>
          <h3 style={styles.formTitle}>{editando ? 'Editar Conductor' : 'Nuevo Conductor'}</h3>
          <form onSubmit={guardar} style={styles.form}>
            <div style={styles.formGrid}>
              <div style={{ ...styles.group, gridColumn: '1 / -1' }}>
                <label style={styles.label}>Nombre completo *</label>
                <input style={styles.input} value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} placeholder="Ej: Juan Pérez" required />
              </div>
              <div style={styles.group}>
                <label style={styles.label}>Cédula</label>
                <input style={styles.input} value={form.cedula} onChange={e => setForm({ ...form, cedula: e.target.value })} placeholder="Ej: 001-1234567-8" />
              </div>
              <div style={styles.group}>
                <label style={styles.label}>Licencia</label>
                <input style={styles.input} value={form.licencia} onChange={e => setForm({ ...form, licencia: e.target.value })} placeholder="Ej: LC-12345" />
              </div>
              <div style={styles.group}>
                <label style={styles.label}>Teléfono</label>
                <input style={styles.input} value={form.telefono} onChange={e => setForm({ ...form, telefono: e.target.value })} placeholder="Ej: 809-555-0000" />
              </div>
              <div style={styles.group}>
                <label style={styles.label}>% Excedente</label>
                <input style={{ ...styles.input, borderColor: '#CC0000' }} type="number" min="0" max="100" step="0.01" value={form.porcentaje_excedente} onChange={e => setForm({ ...form, porcentaje_excedente: e.target.value })} placeholder="Ej: 30" required />
              </div>
              <div style={{ ...styles.group, gridColumn: '1 / -1' }}>
                <label style={styles.label}>Usuario del sistema (opcional)</label>
                <select
                  style={styles.input}
                  value={form.usuario_id}
                  onChange={e => setForm({ ...form, usuario_id: e.target.value })}
                >
                  <option value="">— Sin vincular —</option>
                  {usuariosDisponibles.map(u => (
                    <option key={u.id} value={u.id}>{u.nombre} {u.email ? `· ${u.email}` : ''}</option>
                  ))}
                </select>
                <span style={styles.hint}>Vincula el conductor a su cuenta de acceso a la app</span>
              </div>
            </div>
            <div style={styles.formBtns}>
              <button type="button" style={styles.btnCancel} onClick={resetForm}>Cancelar</button>
              <button type="submit" style={styles.btnSave}>{editando ? 'Guardar Cambios' : 'Crear Conductor'}</button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div style={styles.loading}>Cargando...</div>
      ) : lista.length === 0 ? (
        <div style={styles.empty}>
          <div style={{ fontSize: '48px' }}>👤</div>
          <p>{verInactivos ? 'No hay conductores inactivos.' : 'No hay conductores registrados.'}</p>
          {!verInactivos && esAdmin && <button style={styles.btnAdd} onClick={() => setShowForm(true)}>+ Agregar primer conductor</button>}
        </div>
      ) : (
        <div style={styles.grid}>
          {lista.map(c => (
            <div key={c.id} style={{ ...styles.card, ...(c.activo ? {} : styles.cardInactivo) }}>
              <div style={styles.cardHeader}>
                <div style={styles.avatar}>{c.nombre.charAt(0).toUpperCase()}</div>
                <div>
                  <div style={styles.nombre}>{c.nombre}</div>
                  <div style={styles.vinculado}>
                    {c.usuario_id
                      ? <span style={styles.badgeVinculado}>🔗 Vinculado al sistema</span>
                      : <span style={styles.badgeSinVincular}>Sin cuenta vinculada</span>
                    }
                  </div>
                  {!c.activo && <span style={styles.badgeInactivo}>Inactivo</span>}
                </div>
              </div>
              <div style={styles.cardBody}>
                {c.cedula && <div style={styles.cardRow}><span style={styles.cardLabel}>🪪 Cédula</span><span style={styles.cardVal}>{c.cedula}</span></div>}
                {c.licencia && <div style={styles.cardRow}><span style={styles.cardLabel}>📋 Licencia</span><span style={styles.cardVal}>{c.licencia}</span></div>}
                {c.telefono && <div style={styles.cardRow}><span style={styles.cardLabel}>📞 Teléfono</span><span style={styles.cardVal}>{c.telefono}</span></div>}
                {esAdmin && (
                  <div style={{ ...styles.cardRow, background: '#fff8f8', padding: '8px', borderRadius: '6px', marginTop: '4px' }}>
                    <span style={{ ...styles.cardLabel, color: '#CC0000' }}>💰 % Excedente</span>
                    <span style={{ ...styles.cardVal, color: '#CC0000', fontSize: '16px' }}>{c.porcentaje_excedente}%</span>
                  </div>
                )}
              </div>
              {esAdmin && (
                <div style={styles.cardFooter}>
                  {c.activo ? (
                    <>
                      <button style={styles.btnEdit} onClick={() => abrirEditar(c)}>✏️ Editar</button>
                      <button style={styles.btnDel} onClick={() => eliminar(c.id)}>🗑️ Desactivar</button>
                    </>
                  ) : (
                    <button style={styles.btnRestore} onClick={() => restaurar(c.id)}>♻️ Restaurar</button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
};

const styles = {
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' },
  tabs: { display: 'flex', gap: '8px' },
  tabBtn: { padding: '8px 16px', borderRadius: '8px', border: '1.5px solid #e0e0e0', background: '#fff', color: '#666', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  tabActive: { background: '#1a1a1a', color: '#fff', borderColor: '#1a1a1a' },
  btnAdd: { background: '#CC0000', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 18px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  error: { background: '#fff0f0', border: '1px solid #ffcccc', color: '#CC0000', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' },
  formCard: { background: '#fff', borderRadius: '12px', padding: '24px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #e0e0e0' },
  formTitle: { fontSize: '15px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 16px' },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  group: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '12px', fontWeight: '600', color: '#555' },
  hint: { fontSize: '11px', color: '#888', marginTop: '2px' },
  input: { padding: '10px 12px', borderRadius: '7px', border: '1.5px solid #e0e0e0', fontSize: '14px', outline: 'none', color: '#1a1a1a' },
  formBtns: { display: 'flex', gap: '10px', justifyContent: 'flex-end' },
  btnCancel: { padding: '10px 20px', borderRadius: '7px', border: '1.5px solid #e0e0e0', background: '#fff', color: '#666', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  btnSave: { padding: '10px 20px', borderRadius: '7px', border: 'none', background: '#CC0000', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  loading: { textAlign: 'center', padding: '60px', color: '#666' },
  empty: { textAlign: 'center', padding: '60px', color: '#999', background: '#fff', borderRadius: '12px', border: '1px solid #e0e0e0' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' },
  card: { background: '#fff', borderRadius: '12px', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden' },
  cardInactivo: { opacity: 0.6, borderStyle: 'dashed' },
  cardHeader: { background: '#1a1a1a', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px' },
  avatar: { width: '40px', height: '40px', borderRadius: '50%', background: '#CC0000', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '18px', fontWeight: '800', flexShrink: 0 },
  nombre: { color: '#fff', fontSize: '14px', fontWeight: '700' },
  vinculado: { marginTop: '4px' },
  badgeVinculado: { background: '#1a6b3a', color: '#fff', fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px', display: 'inline-block' },
  badgeSinVincular: { background: '#888', color: '#fff', fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px', display: 'inline-block' },
  badgeInactivo: { background: '#CC0000', color: '#fff', fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px', display: 'inline-block', marginTop: '4px' },
  cardBody: { padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' },
  cardRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' },
  cardLabel: { fontSize: '12px', color: '#888', fontWeight: '500' },
  cardVal: { fontSize: '13px', color: '#1a1a1a', fontWeight: '600' },
  cardFooter: { padding: '12px 16px', borderTop: '1px solid #f0f0f0', display: 'flex', gap: '8px' },
  btnEdit: { flex: 1, padding: '8px', borderRadius: '6px', border: '1.5px solid #e0e0e0', background: '#fff', fontSize: '12px', fontWeight: '600', cursor: 'pointer', color: '#333' },
  btnDel: { flex: 1, padding: '8px', borderRadius: '6px', border: 'none', background: '#fff0f0', fontSize: '12px', fontWeight: '600', cursor: 'pointer', color: '#CC0000' },
  btnRestore: { flex: 1, padding: '8px', borderRadius: '6px', border: 'none', background: '#f0fff4', fontSize: '12px', fontWeight: '600', cursor: 'pointer', color: '#1a6b3a' },
};

export default Conductores;
